import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Heart, Users, Shield, Globe } from 'lucide-react';
import CTA from '../../components/CTA';

const PortstewartHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Portstewart | Retirement Transitions & Burnout Recovery</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Portstewart residents. Retirement transition support, professional burnout recovery, holiday home isolation. CNHC registered. Belfast clinic and online sessions available."
        />
        <meta name="keywords" content="hypnotherapy Portstewart, hypnotherapist Portstewart, retirement anxiety Portstewart, professional burnout Causeway Coast, holiday home isolation, life transition therapy, coastal wellness, therapist Portstewart" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/portstewart-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Portstewart | Retirement Transitions & Burnout Recovery" />
        <meta property="og:description" content="Professional hypnotherapy for Portstewart. Retirement support, burnout recovery, life transitions. Belfast clinic and online sessions available." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Portstewart",
            "description": "CNHC registered hypnotherapy serving Portstewart and the Causeway Coast. Specializing in retirement transition anxiety, professional burnout recovery, holiday home isolation, life transition support, and coastal lifestyle adjustment. In-person sessions available at our Belfast clinic or online sessions for convenience.",
            "url": "https://www.sharonclare-hypnotherapy.com/portstewart-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Portstewart, County Londonderry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Retirement Transition Support",
              "Professional Burnout Recovery",
              "Life Transition Therapy",
              "Holiday Home Isolation Support",
              "Coastal Lifestyle Adjustment"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Portstewart
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Retirement transitions, burnout recovery, and finding peace in coastal life
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Clock className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions from home, or in person at the Belfast and Lisburn clinics
                </p>
              </div>
              <p className="text-gray-600">
                Secure video sessions offer maximum flexibility with no travel at all — ideal for retirees or those with mobility concerns. In-person appointments are available at Belfast Wellness Clinic on the Ormeau Road and at The Arzen Collective in Lisburn.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Portstewart's Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Portstewart is known for its genteel promenade, quieter pace compared to neighbouring Portrush, and strong retirement community. Whether you've moved to Portstewart seeking coastal calm after a high-pressure career, you're navigating the significant life transition of retirement, or you're managing the isolation that can come with holiday home life, mental health support is available nearby. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the adjustment challenges that come with major life changes and the stress of transitioning to a slower rhythm after decades of achievement-focused living.
              </p>
              <p>
                For Portstewart residents, <strong>online sessions via secure video call</strong> are usually the most practical way to work together, and they provide exactly the same therapeutic benefit as face-to-face appointments — ideal if you prefer the comfort of your own home, have mobility concerns, or want to avoid winter weather driving. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, where days and times vary and early evening appointments are available, and at The Arzen Collective, 13B Antrim Street, Lisburn.
              </p>
              <p>
                Portstewart attracts people seeking peace - retirees escaping urban stress, professionals recovering from burnout, holiday home owners wanting coastal refuge. But the transition to a slower pace isn't always easy. Retirement can bring unexpected loss of identity and purpose. Moving from a high-achieving career to "doing nothing" can trigger anxiety rather than relief. Holiday homes can feel isolating when you're disconnected from your primary social network. And the paradox of seeking peace but struggling to actually relax is more common than people realize. These challenges are real, and hypnotherapy offers practical support for navigating them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Portstewart Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps Portstewart Residents
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Retirement Transition Anxiety</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Retirement is supposed to be the reward after decades of work - but many people struggle with the profound identity shift. If your entire adult life was defined by career, achievement, and productivity, suddenly having no structure or external validation can be deeply unsettling. Who are you when you're no longer "Director of X" or "Senior Y"? How do you find purpose without work goals? The loss of routine, social connections from work, and sense of contribution can trigger anxiety and even depression, even though outwardly you have "everything" to be happy about in coastal Portstewart.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy helps with retirement adjustment by addressing the underlying anxiety about loss of identity and purpose. We work on building new sources of meaning beyond career achievements, managing the grief that accompanies retirement (it is a genuine loss), and reframing "not working" as an opportunity rather than emptiness. Many retirees find that once they address the mental resistance, they genuinely enjoy the slower pace and freedom of coastal life. It's about making peace with the transition, not forcing yourself to feel something you don't.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Burnout Recovery</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Many people move to Portstewart seeking recovery from professional burnout - doctors, lawyers, teachers, business owners who gave everything to their careers and reached exhaustion. You chose Portstewart for its calm, genteel character, hoping the coastal setting would provide healing. But burnout doesn't automatically resolve just because you're by the sea. The nervous system dysregulation - hypervigilance, inability to relax, racing thoughts, exhaustion that sleep doesn't fix - persists even when the stressful job is gone.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy is particularly effective for burnout because it directly addresses nervous system regulation. Through deep relaxation techniques and targeted suggestion work, we retrain your stress response so you can actually access the calm that Portstewart offers. It's not about willpower or "trying to relax harder" - it's about teaching your body and brain to exit fight-or-flight mode after months or years of chronic activation. Most people notice significant improvement in sleep, mental clarity, and genuine enjoyment of their surroundings within a few sessions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Holiday Home Isolation & Loneliness</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Owning a holiday home in Portstewart can be wonderful - coastal escape, beautiful promenade walks, peaceful refuge from urban life. But spending extended periods in your Portstewart home can also bring unexpected isolation. You're disconnected from your primary social network, don't have established relationships in the local community (everyone knows you're "not really from here"), and may feel like an outsider in a small town with close-knit year-round residents. The loneliness can be profound, particularly if you're using the holiday home as a solo retreat or if your partner is spending less time there than you are.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy helps with isolation by addressing the anxiety and low mood that accompany loneliness, building resilience for solo time, and working through any guilt about holiday home ownership (valid concern in NI's housing crisis). We also address practical strategies for building connections - but the hypnotherapy component calms the nervous system's distress response to social isolation, making it easier to actually take action rather than withdrawing further.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Aging Anxiety & Health Worries</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Portstewart has a significant older demographic - retirees who moved for quality of life, aging in a beautiful coastal setting. With aging comes increasing health anxiety: awareness of mortality, concerns about declining mobility or cognitive function, managing chronic health conditions, witnessing friends' declining health or passing away, and fear of losing independence. This anxiety can become all-consuming, dominating thoughts and preventing you from enjoying the life you worked so hard to achieve in retirement.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Hypnotherapy doesn't eliminate health concerns (aging is real and health issues are valid), but it can reduce the catastrophic anxiety that makes every symptom feel like a crisis. We work on managing health anxiety, improving sleep quality (poor sleep worsens all anxiety), building acceptance of aging without resignation, and finding peace despite uncertainty. Many older clients report that reducing health anxiety actually improves their quality of life more than any medication - they're able to engage with life again rather than being paralyzed by fear.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Portstewart Residents Choose My Practice
            </h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Convenient Access from Portstewart</h3>
                  <p className="text-gray-700">
                    Online sessions need no travel at all, which makes ongoing support practical from home. If you prefer face to face, the Belfast clinic on the Ormeau Road and the Lisburn clinic on Antrim Street are both available.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Online Sessions Ideal for Retirees</h3>
                  <p className="text-gray-700">
                    Online sessions via secure video call mean no driving in winter weather, no mobility concerns, and complete convenience from your own home. Perfect for retirees, those with health limitations, or holiday home owners wanting support without leaving Portstewart. The therapeutic benefit is identical to in-person sessions.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Understanding of Life Transitions</h3>
                  <p className="text-gray-700">
                    My 15 years in nursing at Belfast City Hospital means I understand life transitions, aging concerns, chronic health anxiety, burnout, and the emotional complexity of major life changes. I've supported countless people through difficult adjustments - this experience informs my hypnotherapy approach.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">CNHC Registered Professional Standards</h3>
                  <p className="text-gray-700">
                    CNHC registration guarantees rigorous training (450+ hours minimum), professional indemnity insurance, and adherence to strict ethical standards. You're working with a properly qualified practitioner who meets professional healthcare standards, not someone with minimal training offering "quick fixes."
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No-Pressure Approach</h3>
                  <p className="text-gray-700">
                    I don't use high-pressure sales tactics or push expensive long-term packages. You choose the pace that works for you - single sessions, a discounted 4-session package, or ongoing support as needed. Many retirees appreciate the flexibility and transparent pricing without feeling pressured into commitments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Portstewart Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I moved to Portstewart for peace, but I'm struggling to actually relax - can hypnotherapy help?</h3>
                <p className="text-gray-700 leading-relaxed">
                  This is incredibly common, especially for people recovering from burnout or adjusting to retirement. Your nervous system is stuck in high-alert mode after years of career stress, so even though you're in a peaceful environment, your body doesn't know how to exit fight-or-flight. Hypnotherapy teaches your nervous system to downregulate, making it possible to actually experience the calm that Portstewart offers. Most people notice they can relax more deeply within a few sessions - it's a skill that improves with practice.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How can hypnotherapy help with retirement adjustment?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Retirement brings major identity shifts - loss of work structure, professional identity, and daily purpose. This can trigger unexpected anxiety or depression, even if you're financially secure and living in beautiful Portstewart. Hypnotherapy addresses the underlying anxiety about identity loss, helps build new sources of meaning, and works through the grief that accompanies retirement (it is a genuine loss, even if it's also an opportunity). We reframe the transition so you can genuinely enjoy retirement rather than feeling lost.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I spend extended periods in my Portstewart holiday home and feel isolated - will therapy help?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes. Holiday home isolation is real - you're disconnected from your primary social network and may not feel integrated into the local community. Hypnotherapy addresses the anxiety and low mood that accompany loneliness, builds resilience for solo time, and works through any guilt about holiday home ownership. We also discuss practical connection strategies. The hypnotherapy component calms your nervous system's distress response to isolation, making it easier to take action rather than withdrawing further.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Can online sessions really be as effective as in-person?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes, absolutely. Research shows online hypnotherapy provides equivalent therapeutic outcomes to face-to-face sessions. The hypnotic trance state works identically via video call - your brain doesn't know the difference between in-person and online in terms of therapeutic benefit. For retirees, those with mobility concerns, or anyone wanting to avoid winter weather driving from Portstewart, online sessions offer complete convenience without compromising effectiveness. Many clients prefer online for the comfort of their own home environment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I have health anxiety that's consuming my retirement - can you help?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Health anxiety is very common in older demographics, particularly after witnessing friends' health decline or experiencing your own health issues. Every symptom feels catastrophic, preventing you from enjoying life. Hypnotherapy can't eliminate health concerns (aging is real), but it significantly reduces the catastrophic anxiety response. We work on managing intrusive health worries, improving sleep (poor sleep worsens all anxiety), and building acceptance of aging without resignation. Many clients report that reducing health anxiety improves quality of life more than any medication - they engage with life again rather than being paralyzed by fear.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How many sessions do I need for retirement transition support?</h3>
                <p className="text-gray-700 leading-relaxed">
                  This varies by individual - some people find 4-6 sessions makes a significant difference, while others benefit from ongoing support over several months during the adjustment period. We review progress regularly and adjust frequency based on your needs and goals. There's no pressure for endless long-term therapy - we work toward specific outcomes like improved sleep, reduced anxiety, better adjustment to retired life. Many retirees start with a 4-session package, then decide if they want further sessions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">I'm concerned about driving to the clinic in winter - what are my options?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Online sessions via secure video call are perfect for this concern. No driving in poor weather, no mobility concerns, complete convenience from your Portstewart home. Many retirees use online sessions during winter months (November-March) when weather is poor, then switch to occasional in-person sessions during better weather if they want face-to-face contact. You have complete flexibility to choose what works for you.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Will people in Portstewart find out I'm seeing a hypnotherapist?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Complete confidentiality is guaranteed by CNHC ethical standards - I cannot share your information with anyone without explicit consent. Small-town privacy concerns are valid, and online sessions provide total discretion (no one sees you arriving at a clinic). Alternatively, our Belfast clinic is outside Portstewart, providing geographic privacy if you prefer in-person sessions. The only exception to confidentiality is if you disclose immediate risk of serious harm, where legal duty of care requires appropriate action.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Is hypnotherapy safe for older adults with health conditions?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes, hypnotherapy is safe for most people, including older adults with chronic health conditions. It's a non-invasive, medication-free approach focused on relaxation and nervous system regulation. If you have diagnosed mental illness (e.g., psychosis, severe bipolar disorder), hypnotherapy may not be appropriate - but for anxiety, stress, adjustment difficulties, burnout, and health worries, it's very safe. My nursing background means I understand medical conditions and can work appropriately within your health context. If you have specific concerns, we discuss them during the free consultation before proceeding.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What if hypnotherapy doesn't work for me?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Hypnotherapy isn't magic - it requires active participation and takes a few sessions to see results. However, most people experience at least some improvement in anxiety, sleep, or stress levels within 3-4 sessions. If you genuinely feel no benefit after 4 sessions, we discuss whether to try a different approach or if another therapy might be more appropriate (I'm honest about this - I don't keep people in ineffective treatment). That said, the vast majority of clients do benefit, particularly for anxiety, retirement adjustment, and burnout recovery. We review progress regularly to ensure you're getting value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What Causeway Coast Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "I retired to Portstewart for the peace but couldn't actually relax after 35 years in high-pressure work. Sharon helped me learn to genuinely unwind. Within a few sessions I was sleeping better and actually enjoying my promenade walks instead of anxiously planning the next project."
                </p>
                <p className="text-sm font-semibold text-gray-900">— P.R., Portstewart retiree</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Retirement brought unexpected anxiety - loss of identity, no daily structure, feeling purposeless. Hypnotherapy helped me navigate that transition. Took a few months but I'm in a much better place now. Actually enjoying retirement rather than feeling lost in it."
                </p>
                <p className="text-sm font-semibold text-gray-900">— J.D., County Londonderry</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Our Portstewart holiday home felt isolating when I spent weeks there alone. Sharon helped me work through the loneliness and guilt (we know housing is a crisis). Online sessions were perfect - no driving, complete privacy. Mental health improved significantly."
                </p>
                <p className="text-sm font-semibold text-gray-900">— M.K., Causeway Coast</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Health anxiety was ruining my retirement - every ache was catastrophic in my mind. Hypnotherapy didn't make health concerns go away but it massively reduced the panic response. I can enjoy life in Portstewart now rather than obsessing over symptoms constantly."
                </p>
                <p className="text-sm font-semibold text-gray-900">— T.S., North Coast</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <p className="text-gray-700 leading-relaxed italic mb-4">
                  "Burned out completely from teaching career, moved to Portstewart for recovery. Thought location alone would heal me but the exhaustion persisted. Sharon's hypnotherapy finally helped my nervous system calm down. Feel like myself again for first time in years."
                </p>
                <p className="text-sm font-semibold text-gray-900">— L.H., Portstewart area</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Immediate Support Resources
            </h2>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <p className="text-gray-700 mb-6">
                If you're experiencing a mental health crisis or need urgent support, please contact:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Lifeline NI:</span>
                  <span>0808 808 8000 (24/7 crisis helpline)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Samaritans:</span>
                  <span>116 123 (24/7 listening service)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Causeway Coast & Glens Crisis Support:</span>
                  <span>Contact Causeway Coast & Glens Borough Council for local mental health services</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Emergency:</span>
                  <span>If in immediate danger, call 999 or go to A&E</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-6">
                Hypnotherapy is not a substitute for emergency mental health care or treatment of diagnosed mental illness. If you have suicidal thoughts or are in crisis, please seek immediate professional help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default PortstewartHypnotherapy;
